<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

// WhatsApp: el contacto eliminó el mensaje "para todos" (se conserva el texto, se muestra tachado)
const isRevoked = computed(() =>
  Boolean(contentAttributes.value?.whatsappRevoked)
);
// WhatsApp: el contacto editó el mensaje (el texto anterior queda en edit_history)
const isEdited = computed(() => Boolean(contentAttributes.value?.edited));
// Un revoke de un mensaje saliente llega por smb_message_echoes: lo borró el negocio, no el contacto.
const revokedLabelKey = computed(() =>
  messageType.value === MESSAGE_TYPES.INCOMING
    ? 'CONVERSATION.MESSAGE_REVOKED_BY_CONTACT'
    : 'CONVERSATION.MESSAGE_REVOKED_BY_BUSINESS'
);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <div
        v-if="renderContent"
        :class="{ 'line-through opacity-60': isRevoked }"
      >
        <FormattedContent :content="renderContent" />
      </div>
      <span v-if="isRevoked" class="text-xs text-n-slate-11">
        {{ $t(revokedLabelKey) }}
      </span>
      <span v-else-if="isEdited" class="text-xs text-n-slate-11">
        {{ $t('CONVERSATION.MESSAGE_EDITED') }}
      </span>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
